import { type Dictionary } from 'lodash';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import { DateTime } from 'luxon';

import type { UserRole } from '@appTypes/esb';
import { organizationTypeId } from '@shared/constants';
import normalizeUnitGender from '@shared/utils/normalizeUnitGender';

import {
  adminAkelaRoles,
  organizationRoles,
  scoutbookRoles,
} from './constants';
import type { DedupedRoleType } from './types';

const isYouthMember = (role: string, roleTypes: UserRole['roleTypes'] = []) =>
  organizationRoles.YOUTH_MEMBER.includes(role) ||
  roleTypes.some(({ roleType }) =>
    organizationRoles.YOUTH_MEMBER.includes(roleType),
  );

const isParentGuardian = (
  role: string,
  roleTypes: UserRole['roleTypes'] = [],
) =>
  organizationRoles.PARENT_GUARDIAN.includes(role) ||
  roleTypes.some(({ roleType }) =>
    organizationRoles.PARENT_GUARDIAN.includes(roleType),
  );

const isAdvancementChair = (role: string) =>
  role === organizationRoles.ADVANCEMENT_COORDINATOR;

const isMeritBadgeCounselor = (role: string) =>
  organizationRoles.MERIT_BADGE_COUNSELOR === role;

const isScoutbookRole = (role: string, roleTypes: UserRole['roleTypes'] = []) =>
  scoutbookRoles.includes(role) ||
  roleTypes.some(({ roleType }) => scoutbookRoles.includes(roleType));

const isKey3 = (role: string) => adminAkelaRoles.includes(role);

const isDistrictExecutive = (role: string) =>
  organizationRoles.DISTRICT_EXECUTIVES.includes(role);

const isCouncilAdmin = (role: string) =>
  organizationRoles.COUNCIL_ADMINS.includes(role);

// From least to most important
const roleLevels = [
  isMeritBadgeCounselor,
  isAdvancementChair,
  isScoutbookRole,
  isKey3,
  isDistrictExecutive,
  isCouncilAdmin,
];

const nonUnitRoleLevels = [
  isMeritBadgeCounselor,
  isAdvancementChair,
  isDistrictExecutive,
  isCouncilAdmin,
];

// council admin > District Executive > key3 > unit advancement chair > everything else
const getRoleLevel = (
  role: string,
  orgTypeId: string,
  roleTypes?: UserRole['roleTypes'],
) => {
  if (!role) {
    return -1;
  }
  const orgRoleLevels =
    +orgTypeId === organizationTypeId.UNIT ? roleLevels : nonUnitRoleLevels;

  const roleLevelsLength = orgRoleLevels.length;
  for (let i = roleLevelsLength; i > 0; i--) {
    const roleLevelFn = orgRoleLevels[i - 1];
    if (roleLevelFn(role, roleTypes)) {
      return i;
    }
  }
  return 0;
};

const getHighestRole = (
  roleA: string,
  roleB: string,
  organizationTypeId: string,
  roleTypes: UserRole['roleTypes'],
) => {
  const roleALevel = getRoleLevel(roleA, organizationTypeId);
  const roleBLevel = getRoleLevel(roleB, organizationTypeId, roleTypes);
  if (roleALevel >= roleBLevel) {
    return roleA;
  }
  return roleB;
};

const normalizeRoles = (
  groups: Dictionary<(UserRole & { isScoutbookRole: boolean })[]>,
) =>
  Object.keys(groups)
    .map(organizationGuid => {
      const group = groups[organizationGuid];
      let isAkelaAdvChair: boolean | undefined;
      const updated: DedupedRoleType = group.reduce((acc, node) => {
        const {
          role,
          roleTypes = [],
          programTypeId,
          programType,
          organizationName,
          organizationTypeId,
          isScoutbookRole,
          acceptGender,
          denDetails = {},
        } = node;
        const denDetailsArr = acc.denDetailsArr || [];
        const isParentOrYouth =
          isYouthMember(role) || isParentGuardian(role, roleTypes);
        // to handle only unit advancement chair from akela and scoutbook since they both use the same string
        if (isAdvancementChair(role) && acc.isAkelaAdvChair === undefined) {
          isAkelaAdvChair = !isScoutbookRole || undefined;
        }
        const updatedRole = {
          ...acc,
          ...node,
          organizationName: acc.organizationName || organizationName,
          organizationTypeId: acc.organizationTypeId || organizationTypeId,
          isAkelaAdvChair,
          role: isParentOrYouth
            ? acc.role
            : getHighestRole(acc.role, role, organizationTypeId, roleTypes),
          roleTypes: isParentOrYouth
            ? acc.roleTypes || []
            : [...(acc.roleTypes || []), ...roleTypes],
          programTypeId: acc.programTypeId || programTypeId,
          programType: acc.programType || programType,
          allRoles: uniq([...(acc.allRoles || []), role]),
          unitGender: acc.unitGender || normalizeUnitGender(acceptGender),
          denDetailsArr: isEmpty(denDetails)
            ? denDetailsArr
            : [...denDetailsArr, denDetails],
        };
        return updatedRole;
      }, {} as DedupedRoleType);
      return updated;
    })
    .filter(item => !isEmpty(item)); //

export const dedupeRoleTypes = (
  roles: Array<UserRole & { isScoutbookRole: boolean }>,
) => {
  const activeRoles = roles.filter(rol => {
    if (rol.expireDate) {
      return (
        DateTime.now().startOf('day') <
        DateTime.fromISO(rol.expireDate).startOf('day')
      );
    }

    return rol;
  });

  // Makes sure roles not from scoutbook are first
  activeRoles.sort((x, y) =>
    x.isScoutbookRole === y.isScoutbookRole ? 0 : x.isScoutbookRole ? 1 : -1,
  );
  const parentAndYouthRoles = activeRoles
    .filter(
      ({ role, roleTypes }) =>
        isYouthMember(role, roleTypes) || isParentGuardian(role, roleTypes),
    )
    .map(organization => ({
      ...organization,
      unitGender: normalizeUnitGender(organization.acceptGender),
    }));

  const groups = groupBy(activeRoles, 'organizationGuid');
  const unitRoles: DedupedRoleType[] = normalizeRoles(groups);

  const parentAndYouthRolesUpdated = parentAndYouthRoles.map(record => {
    const allRoles = unitRoles.find(
      ({ organizationGuid }) => organizationGuid === record.organizationGuid,
    )?.allRoles;

    return {
      ...record,
      allRoles: allRoles,
    };
  });

  return [...parentAndYouthRolesUpdated, ...unitRoles];
};
